import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const monthStart = () => new Date().toISOString().slice(0, 8) + "01";
const today = () => new Date().toISOString().slice(0, 10);

function Kpi({ label, value, sub, tone, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className={`text-xl font-heading font-bold ${tone || ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

export default function Financials() {
  const [range, setRange] = useState({ start: monthStart(), end: today() });
  const [applied, setApplied] = useState(range);

  const { data: d } = useQuery({
    queryKey: ["mech-financials", applied],
    queryFn: async () => (await api.get("/financials", { params: applied })).data,
  });
  const f = d || {};

  return (
    <div className="p-8" data-testid="mech-financials-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl mb-1">Shop Financials</h1>
          <p className="text-muted-foreground">Every dollar in and out — customer jobs, internal fleet costs, oil, expenses.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="label-mono">From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="fin-start" /></div>
          <div><Label className="label-mono">To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="fin-end" /></div>
          <Button onClick={() => setApplied(range)} data-testid="fin-apply">Apply</Button>
        </div>
      </div>

      {/* Customer (external) P&L */}
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Customer jobs (billed work)</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Revenue" value={money(f.revenue)} sub={`${f.external_jobs_done ?? 0} jobs done`} testid="fin-revenue" />
        <Kpi label="Labor rev" value={money(f.labor_revenue)} testid="fin-labor-rev" />
        <Kpi label="Parts rev" value={money(f.parts_revenue)} testid="fin-parts-rev" />
        <Kpi label="Job costs (COGS)" value={money(f.external_cogs)} testid="fin-cogs" />
        <Kpi label="Gross profit" value={money(f.gross_profit)} sub={`${f.gross_margin ?? 0}% margin`} tone="text-green-400" testid="fin-gross" />
        <Kpi label="Avg profit / job" value={money(f.avg_profit_per_job)} testid="fin-avg-profit" />
      </div>

      {/* Internal + overhead */}
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Internal work &amp; overhead</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Rental fleet costs" value={money(f.rental_costs)} tone="text-amber-400" testid="fin-rental-costs" />
        <Kpi label="Dealership costs" value={money(f.dealership_costs)} tone="text-amber-400" testid="fin-dealer-costs" />
        <Kpi label="Oil used" value={`${f.oil_litres_total ?? 0} L`} sub={money(f.oil_cost_total)} testid="fin-oil" />
        <Kpi label="Overhead expenses" value={money(f.expenses_total)} tone="text-red-400" testid="fin-expenses" />
        <Kpi label="Net profit" value={money(f.net_profit)} sub="gross − overhead" tone={Number(f.net_profit) >= 0 ? "text-green-400" : "text-red-400"} testid="fin-net" />
        <Kpi label="Inventory value" value={money(f.inventory_value)} sub="parts + oil on hand" testid="fin-inventory-value" />
      </div>

      {/* Trend chart */}
      <Card className="p-5 mb-6">
        <p className="font-heading mb-3">6-month trend</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={f.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" name="Job costs" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Line dataKey="profit" name="Net profit" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Tech profitability */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border font-heading">Technician performance</div>
          <Table>
            <TableHeader><TableRow><TableHead>Tech</TableHead><TableHead className="text-right">Jobs</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Billed</TableHead><TableHead className="text-right">Profit</TableHead></TableRow></TableHeader>
            <TableBody>
              {(f.by_tech || []).length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No labor logged in range.</TableCell></TableRow>
              ) : f.by_tech.map((t) => (
                <TableRow key={t.tech} data-testid={`tech-row-${t.tech}`}>
                  <TableCell className="font-medium">{t.tech}</TableCell>
                  <TableCell className="text-right font-mono-data">{t.jobs}</TableCell>
                  <TableCell className="text-right font-mono-data">{t.hours}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(t.paid)}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(t.billed)}</TableCell>
                  <TableCell className={`text-right font-mono-data ${t.profit >= 0 ? "text-green-400" : "text-red-400"}`}>{money(t.profit)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Expenses by category */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border font-heading">Expenses by category</div>
          <Table>
            <TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
            <TableBody>
              {(f.expenses_by_category || []).length === 0 ? (
                <TableRow><TableCell colSpan={2} className="text-center py-6 text-muted-foreground">No expenses in range.</TableCell></TableRow>
              ) : f.expenses_by_category.map((e) => (
                <TableRow key={e.category}>
                  <TableCell>{e.category}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(e.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Receivables */}
      <Card className="overflow-hidden mt-6">
        <div className="px-5 py-3 border-b border-border font-heading flex items-center justify-between">
          <span>Money owed to the shop</span>
          <Badge className="border-0 bg-red-500/12 text-red-400" data-testid="fin-receivables-total">{money(f.receivables_total)}</Badge>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Issued</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
          <TableBody>
            {(f.receivables || []).length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No unpaid invoices. 🎉</TableCell></TableRow>
            ) : f.receivables.map((r) => (
              <TableRow key={r.invoice_number}>
                <TableCell className="font-mono-data">{r.invoice_number}</TableCell>
                <TableCell>{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.issue_date)}</TableCell>
                <TableCell><Badge className={`border-0 ${r.status === "Partial" ? "bg-amber-500/12 text-amber-400" : "bg-red-500/12 text-red-400"}`}>{r.status}</Badge></TableCell>
                <TableCell className="text-right font-mono-data">{money(r.total)}</TableCell>
                <TableCell className="text-right font-mono-data text-red-400">{money(r.balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
